import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/api';
import './GlobalSearch.css';

const GlobalSearch = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ projects: [], testCases: [], issues: [] });
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const token = localStorage.getItem('token');

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Perform search
  useEffect(() => {
    if (!query.trim()) {
      setResults({ projects: [], testCases: [], issues: [] });
      return;
    }

    const search = async () => {
      setLoading(true);
      try {

        // Fetch com timeout
        const fetchWithTimeout = (url, timeout = 5000) => {
          return Promise.race([
            fetch(url, {
              headers: { 'Authorization': `Bearer ${token}` }
            }),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Timeout')), timeout)
            )
          ]);
        };

        const results = await Promise.allSettled([
          fetchWithTimeout(getApiUrl('projects')),
          fetchWithTimeout(getApiUrl('test-repository/test-cases')),
          fetchWithTimeout(getApiUrl('issues'))
        ]);

        // Extract successful responses
        let projectsData = [];
        let testCasesData = [];
        let issuesData = [];

        if (results[0].status === 'fulfilled' && results[0].value.ok) {
          projectsData = await results[0].value.json();
        }

        if (results[1].status === 'fulfilled' && results[1].value.ok) {
          testCasesData = await results[1].value.json();
        }

        if (results[2].status === 'fulfilled' && results[2].value.ok) {
          issuesData = await results[2].value.json();
        }

        // Handle different API response formats
        if (projectsData?.data) {
          projectsData = projectsData.data;
        } else if (projectsData?.projects) {
          projectsData = projectsData.projects;
        }

        if (testCasesData?.data) {
          testCasesData = testCasesData.data;
        } else if (testCasesData?.testCases) {
          testCasesData = testCasesData.testCases;
        }

        if (issuesData?.data) {
          issuesData = issuesData.data;
        } else if (issuesData?.issues) {
          issuesData = issuesData.issues;
        }

        // Ensure arrays
        projectsData = Array.isArray(projectsData) ? projectsData : [];
        testCasesData = Array.isArray(testCasesData) ? testCasesData : [];
        issuesData = Array.isArray(issuesData) ? issuesData : [];

        // Filter by query
        const q = query.toLowerCase();
        const filteredProjects = projectsData
          .filter(p => {
            if (!p) return false;
            const name = (p.name || p.title || '').toLowerCase();
            const id = p.id?.toString() || '';
            return name.includes(q) || id.includes(q);
          })
          .slice(0, 5);

        const filteredTestCases = testCasesData
          .filter(tc => {
            if (!tc) return false;
            const title = (tc.title || tc.name || '').toLowerCase();
            const id = tc.id?.toString() || '';
            return title.includes(q) || id.includes(q);
          })
          .slice(0, 5);

        const filteredIssues = issuesData
          .filter(i => {
            if (!i) return false;
            const title = (i.title || i.name || '').toLowerCase();
            const id = i.id?.toString() || '';
            return title.includes(q) || id.includes(q);
          })
          .slice(0, 5);

        setResults({
          projects: filteredProjects,
          testCases: filteredTestCases,
          issues: filteredIssues
        });
        setSelectedIndex(-1);
      } catch (error) {
        console.error('❌ Erro na busca global:', error);
        setResults({ projects: [], testCases: [], issues: [] });
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(search, 150); // Debounce - reduced for faster feedback
    return () => clearTimeout(timer);
  }, [query, token]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
        return;
      }

      const allResults = [
        ...results.projects,
        ...results.testCases,
        ...results.issues
      ];

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % (allResults.length || 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + (allResults.length || 1)) % (allResults.length || 1));
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        const result = allResults[selectedIndex];
        if (result) handleSelectResult(result);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose]);

  const handleSelectResult = (result) => {
    if (result.test_case_id || result.test_case_title) {
      // Issue
      navigate(`/issues`, { state: { focusIssueId: result.id } });
    } else if (result.title && result.id < 1000000) {
      // Test case (heuristic: test cases have smaller IDs)
      navigate(`/test-repository`, { state: { focusTestCaseId: result.id } });
    } else {
      // Project
      navigate(`/projects`, { state: { focusProjectId: result.id } });
    }
    onClose();
  };

  if (!isOpen) return null;

  const allResults = [
    ...results.projects.map(p => ({ ...p, type: 'project' })),
    ...results.testCases.map(tc => ({ ...tc, type: 'testCase' })),
    ...results.issues.map(i => ({ ...i, type: 'issue' }))
  ];

  return (
    <div className="global-search-overlay" onClick={onClose}>
      <div className="global-search-modal" onClick={e => e.stopPropagation()}>
        <div className="search-header">
          <input
            ref={inputRef}
            type="text"
            placeholder="🔍 Buscar projetos, casos de teste, bugs... (Ctrl+K)"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="search-input"
          />
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {query && (
          <div className="search-results">
            {allResults.length === 0 && loading && (
              <div className="loading">Buscando...</div>
            )}

            {!loading && allResults.length === 0 && (
              <div className="no-results">Nenhum resultado encontrado para "{query}"</div>
            )}

            {allResults.length > 0 && (
              <>
                {results.projects.length > 0 && (
                  <div className="results-group">
                    <h3 className="group-title">📁 Projetos</h3>
                    {results.projects.map((project, idx) => (
                      <button
                        key={project.id}
                        className={`result-item ${selectedIndex === idx ? 'selected' : ''}`}
                        onClick={() => handleSelectResult(project)}
                      >
                        <div className="result-name">{project.name}</div>
                        <div className="result-meta">#{project.id}</div>
                      </button>
                    ))}
                  </div>
                )}

                {results.testCases.length > 0 && (
                  <div className="results-group">
                    <h3 className="group-title">📋 Casos de Teste</h3>
                    {results.testCases.map((testCase, idx) => (
                      <button
                        key={testCase.id}
                        className={`result-item ${selectedIndex === results.projects.length + idx ? 'selected' : ''}`}
                        onClick={() => handleSelectResult(testCase)}
                      >
                        <div className="result-name">{testCase.title || testCase.name}</div>
                        <div className="result-meta">#{testCase.id}</div>
                      </button>
                    ))}
                  </div>
                )}

                {results.issues.length > 0 && (
                  <div className="results-group">
                    <h3 className="group-title">🐛 Bugs / Melhorias</h3>
                    {results.issues.map((issue, idx) => (
                      <button
                        key={issue.id}
                        className={`result-item ${selectedIndex === results.projects.length + results.testCases.length + idx ? 'selected' : ''}`}
                        onClick={() => handleSelectResult(issue)}
                      >
                        <div className="result-name">{issue.title}</div>
                        <div className="result-meta">#{issue.id} • {issue.type || 'bug'}</div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {!query && (
              <div className="search-hint">
                <p>💡 Digite para buscar em projetos, casos de teste e bugs</p>
                <p style={{ fontSize: '0.85em', color: '#999', marginTop: '8px' }}>
                  Use ↑↓ para navegar, Enter para selecionar, Esc para fechar
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalSearch;
